<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Add payment') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <DatePicker v-model="paymentDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
          </div>
          <div class="form-group">
            <label for="method">{{ _('Payment method') }}</label>
            <select class="form-control" id="method" v-model="payment.method">
              <optgroup :label="_('Enabled payment methods')">
                <option v-for="pt in payments" :value="pt">{{ pt.name }}</option>
              </optgroup>
              <optgroup :label="_('Vouchers')">
                <option v-for="pt in vouchers" :value="pt">{{ pt.name }}</option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label for="amount">{{ _('Amount') }}</label> 
  					<input class="form-control" type="number" data-android-inputmode="decimal" id="amount" v-model="payment.amount" :lang="locale"> 
          </div>
          <div class="form-group" v-if="isMobilePaySelected && isNegativeAmount">
            <label for="mobilepay_reference">{{ _('MobilePay reference') }}</label> 
            <div class="input-group">
              <input class="form-control" type="text" id="mobilepay_reference" v-model="payment.mobilepay_reference" :lang="locale">
              <div class="input-group-addon" @click="loadAttempt(payment.mobilepay_reference)" :class="{ 'disabled': !payment.mobilepay_reference }" :style="!isValidReferenceCode ? 'pointer-events: none; opacity: 0.6;' : 'cursor: pointer;'">
                <span class="fa fa-check"></span> {{ _('Check') }}
              </div>
            </div>
            <p class="help-block"><i class="fa fa-fw fa-info-circle"></i> {{ _('If you wish to perform a refund to MobilePay, enter the reference of the payment attempt here.') }}</p>
            <div v-if="attempt && attempt.events">
              <p>{{ _('Remaining value') }}: {{ remainingRefundableAmount }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i class="fa fa-check fa-fw"></i> {{ _('Ok') }}</button>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  props: ['missingAmount', 'paymentTypes', 'locale', 'baseDomain', 'shopId', 'apiKey'],
  data () {
    const _ = useNuxtApp().$translate
    return {
      attempt: {},
      payment: {
        date: DateTime.now().startOf('day').toFormat('yyyy-MM-dd'),
        method: this.paymentTypes.find(pt => pt.type === 'bill'),
        amount: parseFloat(this.missingAmount),
        voucher: null,
        mobilepay_reference: null
      }
    }
  },
  computed: {
    ...mapGetters(['dateFormat']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    isValidReferenceCode () {
      return this.payment.mobilepay_reference && this.payment.mobilepay_reference.match(/^[a-z0-9]{3,32}-[a-z]-\d+-\d+$/)
    },
    isMobilePaySelected () {
      return this.payment && this.payment.method && this.payment.method.type === 'mobilepay'
    },
    isNegativeAmount () {
      return this.payment && this.payment.amount && this.payment.amount < 0
    },
    isSaveButtonEnabled () {
      if (this.payment && this.payment.method && this.payment.method.type === 'mobilepay' && this.payment.mobilepay_reference) {
        if (!this.attempt || !this.attempt.payment) {
          return false
        }
        if (this.remainingRefundableAmount < Math.abs(this.payment.amount)) {
          return false
        }
      }
      return this.payment && this.payment.date && this.payment.method && !isNaN(parseFloat(this.payment.amount))
    },
    paymentDate: {
      get () {
        if (!this.payment || !this.payment.date) {
          return new Date()
        }
        return DateTime.fromFormat(this.payment.date, 'yyyy-MM-dd').toJSDate()
      },
      set (newValue) {
        this.payment.date = DateTime.fromJSDate(newValue).toFormat('yyyy-MM-dd')
      }
    },
    payments () {
      return this.paymentTypes.filter(pt => pt.type !== 'voucher').sort((a, b) => a.name && b.name && a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
    },
    vouchers () {
      return this.paymentTypes.filter(pt => pt.type === 'voucher').sort((a, b) => a.name && b.name && a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
    },
    remainingRefundableAmount () {
      if (!this.attempt || !this.attempt.events || !this.attempt.events.length) {
        return 0
      }
      const totalCaptured = this.attempt.events.filter(event => event.name === 'CAPTURED').reduce((sum, event) => sum + event.amount.value, 0)
      if (this.attempt.events && this.attempt.events.some(event => event.name === 'REFUNDED')) {
        const totalRefunded = this.attempt.events.filter(event => event.name === 'REFUNDED').reduce((sum, event) => sum + event.amount.value, 0)
        return (totalCaptured - totalRefunded) / 100
      }
      return totalCaptured / 100
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    clearAttempt() {
      this.attempt = {}
    },
    loadAttempt(reference) {
      return new Promise(async (resolve, reject) => {
        try {
          this.loading = true
          const response = await axios.get(this.baseDomain + this.shopId + '/mobilepayhandler/' + reference + '.json?apikey=' + this.apiKey + '&plugin=app')
          this.attempt = response.data
          resolve(this.attempt)
        } catch (e) {
          reject()
        } finally {
          this.loading = false
        }
      })
    },
    async save() {
      let payment = {
        paymentdate: DateTime.fromFormat(this.payment.date, 'yyyy-MM-dd', { zone: 'Europe/Helsinki' })
          .startOf('day')
          .toSeconds(),
        method: this.payment.method.type,
        amount: this.payment.amount
      }
      if (this.payment.method.type === 'voucher') {
        payment.voucher = {
          name: this.payment.method.name,
          id: this.payment.method.voucher_id
        }
      }
      if (this.payment.method.type === 'mobilepay' && this.payment.mobilepay_reference) {
        payment.mobilepay_reference = this.payment.mobilepay_reference
      }
      this.$emit('addPayment', payment)
    }
  },
  watch: {
    'payment.mobilepay_reference': function(newVal) {
      this.clearAttempt()
    }
  }
}
</script>